<template>
  <div class="ask-payment-element">
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="2" y1="10" x2="22" y2="10"></line>
        </svg>
        <span>Demande de Paiement</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="element-content">
      <!-- Provider Selection -->
      <div class="form-group">
        <label class="input-label">
          Moyen de paiement
        </label>
        <div class="input-wrapper">
          <select 
            v-model="localProvider" 
            @change="updateProvider"
            class="form-select"
          >
            <option value="wave">Wave Mobile Money</option>
            <option value="orange_money">Orange Money (Bientôt)</option>
          </select>
        </div>
      </div>

      <!-- Amount Input -->
      <div class="form-group">
        <label class="input-label">Montant</label>
        <div class="merged-input-group">
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="localAmount" 
              @blur="handleBlur($event, 'amount')"
              @keyup="trackCursor($event, 'amount')"
              @click="trackCursor($event, 'amount')"
              placeholder="Ex: 500 ou {{variable}}"
              class="form-input amount-input merged-top"
              ref="amountInput"
            />
            <div class="currency-select-wrapper merged-top-currency">
              <select v-model="localCurrency" @change="updateCurrency" class="currency-select">
                <option value="FCFA">FCFA</option>
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
          </div>
          <div class="variable-inserter">
            <VariableSelector 
              v-model="selectedVarAmount"
              :variables="variables"
              placeholder="+ Insérer une variable"
              @update:modelValue="(v) => handleVariableSelect('amount', v)"
            />
          </div>
        </div>
      </div>

      <!-- Reference/Description Input -->
      <div class="form-group">
        <label class="input-label">Description / Référence</label>
        <div class="merged-input-group">
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="localReference" 
              @blur="handleBlur($event, 'reference')"
              @keyup="trackCursor($event, 'reference')"
              @click="trackCursor($event, 'reference')"
              placeholder="Ex: Abonnement Premium ou {{variable}}"
              maxlength="100"
              class="form-input merged-top"
              ref="referenceInput"
            />
          </div>
          <div class="variable-inserter">
            <VariableSelector 
              v-model="selectedVarReference"
              :variables="variables"
              placeholder="+ Insérer une variable"
              @update:modelValue="(v) => handleVariableSelect('reference', v)"
            />
          </div>
        </div>
      </div>

      <!-- Separator -->
      <hr class="element-divider" />

      <!-- Success Redirection -->
      <div class="form-group">
        <label class="input-label success-label">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          En cas de SUCCÈS, aller vers :
        </label>
        <div class="select-wrapper success-wrapper" style="border: none; padding: 0; background: transparent;">
          <BlockSelector
            v-model="localSuccessBlock"
            :idBot="idBot"
            :allowEmpty="true"
            placeholder="-- Continuer le flux actuel --"
            @change="updateSuccessBlock"
          />
        </div>
      </div>

      <!-- Error Redirection -->
      <div class="form-group">
        <label class="input-label error-label">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          En cas d'ÉCHEC, aller vers :
        </label>
        <div class="select-wrapper error-wrapper" style="border: none; padding: 0; background: transparent;">
          <BlockSelector
            v-model="localErrorBlock"
            :idBot="idBot"
            :allowEmpty="true"
            placeholder="-- Arrêter le flux --"
            @change="updateErrorBlock"
          />
        </div>
      </div>

      <!-- Separator -->
      <hr class="element-divider" />

      <!-- Webhook URLs Toggle -->
      <div class="form-group">
        <label class="checkbox-toggle-label" @click="toggleWebhooks">
          <div class="checkbox-toggle" :class="{ active: showWebhooks }">
            <div class="checkbox-toggle-knob"></div>
          </div>
          <span class="checkbox-toggle-text">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            Activer les webhooks (liens à appeler)
          </span>
        </label>
      </div>

      <!-- Webhook URL fields (shown when toggle is on) -->
      <template v-if="showWebhooks">
        <!-- Success Webhook URL -->
        <div class="form-group">
          <label class="input-label success-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            Lien à appeler en cas de SUCCÈS :
          </label>
          <div class="input-wrapper">
            <input
              type="url"
              v-model="localSuccessUrl"
              @blur="updateSuccessUrl"
              placeholder="https://exemple.com/paiement/success"
              class="form-input url-input"
            />
          </div>
        </div>

        <!-- Error Webhook URL -->
        <div class="form-group">
          <label class="input-label error-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            Lien à appeler en cas d'ÉCHEC :
          </label>
          <div class="input-wrapper">
            <input
              type="url"
              v-model="localErrorUrl"
              @blur="updateErrorUrl"
              placeholder="https://exemple.com/paiement/echec"
              class="form-input url-input"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import BlockSelector from '@/components/common/BlockSelector.vue';
import VariableSelector from '@/components/common/VariableSelector.vue';

export default {
  name: 'AskForPaymentElement',
  components: {
    BlockSelector,
    VariableSelector
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    idBot: {
      type: String,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localProvider: 'wave',
      localAmount: '100',
      localReference: '',
      localCurrency: 'FCFA',
      localSuccessBlock: '',
      localErrorBlock: '',
      localSuccessUrl: '',
      localErrorUrl: '',
      showWebhooks: false,
      blocks: [],
      isLoading: false,
      cursorPosition: null,
      currentInputForVar: null,
      selectedVarAmount: null,
      selectedVarReference: null
    }
  },
  watch: {
    element: {
      deep: true,
      immediate: true,
      handler(newVal, oldVal) {
        // Prevent infinite loop if element is deeply equal
        if (!oldVal || JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.initData();
        }
      }
    }
  },
  methods: {
    initData() {
      if (!this.element) return;
      this.localProvider = this.element.provider || 'wave';
      this.localAmount = this.element.amount || '100';
      this.localReference = this.element.client_reference || '';
      this.localCurrency = this.element.currency || 'FCFA';
      this.localSuccessBlock = this.element.success_block || '';
      this.localErrorBlock = this.element.error_block || '';
      this.localSuccessUrl = this.element.success_url || '';
      this.localErrorUrl = this.element.error_url || '';
      this.showWebhooks = !!(this.element.success_url || this.element.error_url);
    },
    trackCursor(event, field) {
      if (event && event.target) {
        this.cursorPosition = event.target.selectionStart;
        this.currentInputForVar = field;
      }
    },
    handleBlur(event, field) {
      this.trackCursor(event, field);
      if (field === 'amount') this.updateAmount();
      if (field === 'reference') this.updateReference();
    },
    handleVariableSelect(field, varId) {
      // Reject non-string values (like bubbling native DOM Events)
      if (!varId || typeof varId !== 'string') return;
      
      const variableText = `{{${varId}}}`;
      
      if (field === 'amount') {
        const textStr = (this.localAmount || '').toString();
        const pos = (this.currentInputForVar === 'amount' && this.cursorPosition !== null) ? this.cursorPosition : textStr.length;
        
        this.localAmount = textStr.slice(0, pos) + variableText + textStr.slice(pos);
        this.updateAmount();
        
        this.$nextTick(() => {
          this.selectedVarAmount = null;
          const input = this.$refs.amountInput;
          if (input) {
            const newPos = pos + variableText.length;
            input.focus();
            input.setSelectionRange(newPos, newPos);
            this.cursorPosition = newPos;
          }
        });
      } else if (field === 'reference') {
        const textStr = (this.localReference || '').toString();
        const pos = (this.currentInputForVar === 'reference' && this.cursorPosition !== null) ? this.cursorPosition : textStr.length;
        
        this.localReference = textStr.slice(0, pos) + variableText + textStr.slice(pos);
        this.updateReference();
        
        this.$nextTick(() => {
          this.selectedVarReference = null;
          const input = this.$refs.referenceInput;
          if (input) {
            const newPos = pos + variableText.length;
            input.focus();
            input.setSelectionRange(newPos, newPos);
            this.cursorPosition = newPos;
          }
        });
      }
    },
    updateAmount() {
      this.updateElement({ amount: this.localAmount });
    },
    updateReference() {
      this.updateElement({ client_reference: this.localReference });
    },
    updateCurrency() {
      this.updateElement({ currency: this.localCurrency });
    },
    updateSuccessBlock() {
      this.updateElement({ success_block: this.localSuccessBlock });
    },
    updateErrorBlock() {
      this.updateElement({ error_block: this.localErrorBlock });
    },
    toggleWebhooks() {
      this.showWebhooks = !this.showWebhooks;
      if (!this.showWebhooks) {
        this.localSuccessUrl = '';
        this.localErrorUrl = '';
        this.updateElement({ success_url: null, error_url: null });
      }
    },
    updateSuccessUrl() {
      this.updateElement({ success_url: this.localSuccessUrl || null });
    },
    updateErrorUrl() {
      this.updateElement({ error_url: this.localErrorUrl || null });
    },
    
    updateElement(changes) {
      this.$emit('update', { ...this.element, ...changes });
    }
  }
}
</script>

<style scoped>
.ask-payment-element {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.ask-payment-element:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo Dark Green */
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1; /* Peelo Mint */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.element-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  font-size: 13px;
  font-weight: 600;
  color: #143028;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.label-hint {
  font-size: 11px;
  font-weight: normal;
  color: #64748b;
  opacity: 0.7;
}

.form-input, .form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  color: #143028;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.form-input:focus, .form-select:focus {
  outline: none;
  border-color: #143028;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(218, 255, 209, 0.3);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-select-wrapper {
  position: absolute;
  right: 6px;
  top: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
}

.currency-select {
  height: 100%;
  padding: 0 12px;
  border: none;
  border-left: 1.5px solid #e2e8f0;
  background: rgba(20, 48, 40, 0.03);
  font-size: 13px;
  font-weight: 700;
  color: #143028;
  cursor: pointer;
  border-radius: 0 10px 10px 0;
  outline: none;
  transition: all 0.2s;
}

.currency-select:hover {
  background: rgba(218, 255, 209, 0.2);
}

.amount-input {
  padding-right: 90px !important;
}

.element-divider {
  border: 0;
  border-top: 1px solid #f1f5f9;
  margin: 10px 0;
}

.success-label {
  color: #16a34a;
}

.success-wrapper .form-select {
  border-color: rgba(22, 163, 74, 0.2);
  background-color: rgba(22, 163, 74, 0.02);
}

.error-label {
  color: #dc2626;
}

.error-wrapper .form-select {
  border-color: rgba(220, 38, 38, 0.2);
  background-color: rgba(220, 38, 38, 0.02);
}

/* Variables suggestion styles */
.variable-suggestions {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  width: 100% !important;
  max-height: 200px !important;
  background: #ffffff !important;
  border: 1.5px solid #143028 !important;
  border-top: none !important;
  border-radius: 0 0 16px 16px !important;
  box-shadow: 0 15px 30px rgba(20, 48, 40, 0.15) !important;
  z-index: 1000 !important;
  margin-top: -4px !important;
  overflow-y: auto;
  overflow-x: hidden;
}

.variable-suggestion-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 18px;
  background: none;
  border: none;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #143028;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.variable-suggestion-item:hover {
  background: #daffd1;
  color: #143028;
  padding-left: 22px;
}

.no-results {
  padding: 16px;
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
  font-style: italic;
}

/* Webhook toggle */
.checkbox-toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.checkbox-toggle {
  position: relative;
  width: 40px;
  height: 22px;
  background: #e2e8f0;
  border-radius: 11px;
  transition: background 0.25s ease;
  flex-shrink: 0;
}

.checkbox-toggle.active {
  background: #143028;
}

.checkbox-toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border-radius: 50%;
  transition: transform 0.25s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}

.checkbox-toggle.active .checkbox-toggle-knob {
  transform: translateX(18px);
}

.checkbox-toggle-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #143028;
}

.url-input {
  font-size: 13px;
}

/* Custom Scrollbar for suggestions */
.variable-suggestions::-webkit-scrollbar {
  width: 6px;
}

.variable-suggestions::-webkit-scrollbar-track {
  background: #f8fafc;
}

.variable-suggestions::-webkit-scrollbar-thumb {
  background: #143028;
  border-radius: 10px;
}

/* Merged fields styles */
.merged-input-group {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.merged-top {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.merged-top-currency select {
  border-bottom-right-radius: 0 !important;
}

.variable-inserter ::v-deep(.variable-multiselect) {
  min-height: 36px !important;
}

.variable-inserter ::v-deep(.variable-multiselect .multiselect__tags) {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  border-top: none !important;
  background: #f8fafc;
  min-height: 36px !important;
  padding: 6px 40px 6px 12px !important;
}

.variable-inserter ::v-deep(.variable-multiselect .multiselect__placeholder) {
  color: #143028;
  font-weight: 600;
  font-size: 13px;
  opacity: 0.8;
}

.variable-inserter ::v-deep(.variable-multiselect .multiselect__select) {
  height: 36px !important;
  padding: 0 !important;
}

.variable-inserter ::v-deep(.variable-multiselect .peelo-caret) {
  color: #143028;
}

.variable-inserter:hover ::v-deep(.variable-multiselect .multiselect__tags) {
  background: rgba(218, 255, 209, 0.3);
}
</style>
